import clsx from 'clsx';
import EmblaCarousel from 'components/carousel2025/EmblaCarousel';
import Button from 'components/common/Button';
import useEmblaCarousel from 'embla-carousel-react';
import EditableLotteryEntry from 'enter2026late/EditableLotteryEntry';
import { useDrawStore } from 'hooks/stores/useDrawStore';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { singularOrPlural } from 'utils/common';

import styles from './LotteryEntryPickerSlidingModal.module.css';
import SlidingModal, { Props as SlidingModalProps } from './SlidingModal';

interface UseLotteryEntryPickerSlidingModalOptions {
  slideAmount?: SlidingModalProps['slideAmount'];
}

export const useLotteryEntryPickerSlidingModal = (
  args?: UseLotteryEntryPickerSlidingModalOptions,
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [initialIndex, setInitialIndex] = useState(0);
  // isDone starts as false initially
  const [isDone, setIsDone] = useState(false);
  const [seenNullBall, setSeenNullBall] = useState(false);
  const startEditing = useCallback((index: number) => {
    setSeenNullBall(false);
    setIsDone(false);
    setIsOpen(true);
    setInitialIndex(index);
  }, []);
  const enterStore = useEnterStore();

  if (!seenNullBall || isDone) {
    // Check if any of the balls still need to be picked
    if (
      enterStore.lines.some(
        (x) =>
          x.main.some((y) => y === null) || x.bonus.some((y) => y === null),
      )
    ) {
      setSeenNullBall(true);
      setIsDone(false);
    }
  } else {
    // If no balls need picking, then we can set isDone to true
    if (
      !enterStore.lines.some(
        (x) =>
          x.main.some((y) => y === null) || x.bonus.some((y) => y === null),
      ) &&
      !isDone
    ) {
      setIsDone(true);
    }
  }

  return {
    startEditing: startEditing,
    render: () => (
      <LotteryEntryPickerSlidingModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        carouselIndex={initialIndex}
        isDone={isDone}
        slideAmount={args?.slideAmount}
      />
    ),
  };
};

interface Props extends SlidingModalProps {
  carouselIndex?: number | undefined;
  isDone: boolean;
}

export const LotteryEntryPickerSlidingModal = observer((props: Props) => {
  const { numberSetup } = useDrawStore();
  const enterStore = useEnterStore();
  const emblaCarouselInfo = useEmblaCarousel({});
  const emblaApi = emblaCarouselInfo[1];
  const [selectedCarouselIndex, setSelectedCarouselIndex] = useState<
    number | undefined
  >(undefined);
  const bonusBallArea = useRef<HTMLParagraphElement>(null);
  const [disabledBallClick, setDisabledBallClick] = useState(0);
  const [disabledBonusBallClick, setDisabledBonusBallClick] = useState(0);
  const hasPulsed = useRef(false);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const onSelect = () => {
      setSelectedCarouselIndex(emblaApi.selectedScrollSnap());
      setDisabledBallClick(0);
      setDisabledBonusBallClick(0);
    };
    onSelect();
    emblaApi.on('select', onSelect).on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect).off('reInit', onSelect);
    };
  }, [emblaApi]);

  useEffect(() => {
    if (
      props.carouselIndex !== undefined &&
      props.carouselIndex !== emblaApi?.selectedScrollSnap()
    ) {
      emblaApi?.scrollTo(props.carouselIndex, true);
    }
  }, [props.carouselIndex, emblaApi]);
  const pickMessages = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (props.isOpen) {
      hasPulsed.current = true;
    } else {
      hasPulsed.current = false;
    }
  }, [props.isOpen]);

  const currentEntry =
    selectedCarouselIndex === undefined
      ? undefined
      : enterStore.lines[selectedCarouselIndex];

  return (
    <SlidingModal
      {...props}
      background="darkBlue"
      customCloseButton={
        props.isDone
          ? (onClose) => (
              <div className={styles.doneButtonContainer}>
                <Button
                  buttonStyle="SecondaryTeal"
                  buttonSize="Small"
                  onClick={onClose}
                >
                  Done
                </Button>
              </div>
            )
          : undefined
      }
    >
      <div className={clsx(styles.container, 'daymadeTypographySRegular')}>
        <h2 className={clsx('daymadeTypographyMLBlack', styles.heading)}>
          Your lucky numbers
        </h2>
        <p>
          Pick <strong>{numberSetup.main.length}</strong>{' '}
          {singularOrPlural(numberSetup.main.length, 'number', 'numbers')} and{' '}
          <strong>{numberSetup.bonus.length}</strong> bonus for{' '}
          {enterStore.lines.length === 1 ? 'your entry' : 'each entry'}
        </p>
        <EmblaCarousel
          className={clsx(
            styles.entryCarousel,
            styles['entryCount' + enterStore.lines.length],
          )}
          emblaCarouselInfo={emblaCarouselInfo}
          pips2026
          showPipNumbers
          items={enterStore.lines.map((x, i) => (
            <EditableLotteryEntry
              lineNumber={i + 1}
              onRandomiseClick={() => enterStore.generateLineRandomNumbers(i)}
              main={x.main}
              bonus={x.bonus}
              background="onDarkBlue"
              onEditClick={(data) => {
                if (data === 'BONUS_BALL' && bonusBallArea.current) {
                  bonusBallArea.current.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            />
          ))}
        >
          <button
            className={styles.previous}
            aria-label="Previous entry"
            onClick={() => {
              emblaCarouselInfo[1]?.scrollPrev();
            }}
          >
            {prevNextArrow()}
          </button>
          <button
            className={styles.next}
            aria-label="Next entry"
            onClick={() => {
              emblaCarouselInfo[1]?.scrollNext();
            }}
          >
            {prevNextArrow()}
          </button>
        </EmblaCarousel>
        {currentEntry && (
          <>
            <div
              // Use a key on the NumberPicker container, otherwise we sometimes get spurious animations when
              // moving between carousel items
              key={selectedCarouselIndex}
              className={clsx(
                styles.pickerContainer,
                props.isOpen &&
                  !hasPulsed.current &&
                  styles.pickerContainerPulsing,
              )}
            >
              <div className={styles.pickMessages} ref={pickMessages}>
                <p>
                  Pick <strong>{numberSetup.main.length}</strong>{' '}
                  {singularOrPlural(
                    numberSetup.main.length,
                    'number',
                    'numbers',
                  )}
                </p>
                {disabledBallClick > 0 && (
                  <p className={styles.deselectMessage}>
                    Deselect to pick a number
                  </p>
                )}
              </div>

              <NumberPicker
                ballErrorCount={disabledBallClick}
                maxBallNumber={numberSetup.main[0]}
                ballCount={numberSetup.main.length}
                selectedBalls={currentEntry.main}
                onBallClicked={(ballNumber) => {
                  if (selectedCarouselIndex === undefined) {
                    return;
                  }

                  const existingIndex = enterStore.lines[
                    selectedCarouselIndex
                  ].main.findIndex((x) => x === ballNumber);
                  if (existingIndex >= 0) {
                    enterStore.setLineNumber(
                      selectedCarouselIndex,
                      existingIndex,
                      null,
                    );
                    setDisabledBallClick(0);
                  } else {
                    const missingIndex = enterStore.lines[
                      selectedCarouselIndex
                    ].main.findIndex((x) => x === null);
                    if (missingIndex >= 0) {
                      enterStore.setLineNumber(
                        selectedCarouselIndex,
                        missingIndex,
                        ballNumber,
                      );
                      setDisabledBallClick(0);
                    } else {
                      setDisabledBallClick((x) => x + 1);
                    }
                  }
                }}
              />
              <p ref={bonusBallArea}>
                Now pick{' '}
                {numberSetup.bonus.length === 1
                  ? 'a'
                  : numberSetup.bonus.length}{' '}
                <strong>bonus</strong>{' '}
                {singularOrPlural(
                  numberSetup.bonus.length,
                  'number',
                  'numbers',
                )}
              </p>
              <NumberPicker
                isBonusBall
                ballErrorCount={disabledBonusBallClick}
                maxBallNumber={numberSetup.bonus[0]}
                ballCount={numberSetup.bonus.length}
                selectedBalls={currentEntry.bonus}
                onBallClicked={(ballNumber: number) => {
                  if (selectedCarouselIndex === undefined) {
                    return;
                  }

                  const currentBonusBall =
                    enterStore.lines[selectedCarouselIndex].bonus[0];
                  if (currentBonusBall === ballNumber) {
                    enterStore.setLineBonusNumber(
                      selectedCarouselIndex,
                      0,
                      null,
                    );
                    setDisabledBonusBallClick(0);
                  } else if (currentBonusBall === null) {
                    enterStore.setLineBonusNumber(
                      selectedCarouselIndex,
                      0,
                      ballNumber,
                    );
                    setDisabledBonusBallClick(0);
                  } else {
                    setDisabledBonusBallClick((x) => x + 1);
                  }
                }}
              />
            </div>
          </>
        )}
      </div>
    </SlidingModal>
  );
});

interface NumberPickerProps {
  isBonusBall?: boolean;
  ballErrorCount: number;
  maxBallNumber: number;
  ballCount: number;
  selectedBalls: (number | null)[];
  onBallClicked: (ballNumber: number) => void;
}
const NumberPicker = observer((props: NumberPickerProps) => {
  const balls = Array.from({ length: props.maxBallNumber }, (_, i) => i + 1);
  const selectedBallMap = new Set(props.selectedBalls.filter((x) => x != null));
  const isSelectionFull = props.ballCount === selectedBallMap.size;

  return (
    <div
      className={clsx(
        styles.ballList,
        props.ballErrorCount === 0
          ? undefined
          : props.ballErrorCount % 2 == 0
            ? styles.ballListError
            : styles.ballListError2,
      )}
    >
      {balls.map((ballNumber) => (
        <span key={ballNumber} className={styles.outer}>
          <button
            type="button"
            className={clsx(
              'buttonReset',
              'daymadeTypographySBold',
              styles.ball,
              props.isBonusBall && styles.bonusBall,
              selectedBallMap.has(ballNumber) && styles.ballSelected,
              isSelectionFull &&
                !selectedBallMap.has(ballNumber) &&
                styles.ballDisabled,
            )}
            aria-pressed={selectedBallMap.has(ballNumber)}
            onClick={() => props.onBallClicked(ballNumber)}
          >
            <span>
              <span>{ballNumber}</span>
            </span>
          </button>
        </span>
      ))}
      {/* The last row of a flex-wrap flexbox will size the items to fill the available space. This means that
      if the rest of the flexbox shows 8 balls, and the last row only needs to show 2 balls, then those 2 balls
      will appear around 50% width. Therefore, we stuff a bunch of extra elements into the flexbox to force that
      last row's items to be the same width as other items. Since the extra elements are empty, they have no height, so do not cause a new row to be rendered */}
      <span className={styles.outer}></span>
      <span className={styles.outer}></span>
      <span className={styles.outer}></span>
      <span className={styles.outer}></span>
      <span className={styles.outer}></span>
      <span className={styles.outer}></span>
      <span className={styles.outer}></span>
      <span className={styles.outer}></span>
      <span className={styles.outer}></span>
      <span className={styles.outer}></span>
    </div>
  );
});

function prevNextArrow() {
  return (
    <svg
      width="5"
      height="9"
      viewBox="0 0 5 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1.73375 4.06975L4.58075 6.91675C4.71458 7.05058 4.7815 7.22092 4.7815 7.42775C4.7815 7.63458 4.71458 7.80492 4.58075 7.93875C4.44692 8.07258 4.27658 8.1395 4.06975 8.1395C3.86292 8.1395 3.69258 8.07258 3.55875 7.93875L0.20075 4.58075C0.12775 4.50775 0.0761631 4.42867 0.0459898 4.3435C0.0158165 4.25833 0.000486667 4.16708 0 4.06975C0 3.97242 0.0153298 3.88117 0.0459898 3.796C0.0766498 3.71083 0.128236 3.63175 0.20075 3.55875L3.55875 0.20075C3.69258 0.0669164 3.86292 0 4.06975 0C4.27658 0 4.44692 0.0669164 4.58075 0.20075C4.71458 0.334583 4.7815 0.504917 4.7815 0.71175C4.7815 0.918583 4.71458 1.08892 4.58075 1.22275L1.73375 4.06975Z"
        fill="currentColor"
      />
    </svg>
  );
}
